/**
 * Formatting.
 *
 * Money arrives as an integer in the currency's minor unit, and the size of
 * that unit comes from the club — 1234 is $12.34 in USD, ¥1,234 in JPY, and
 * 1.234 KWD. Nothing here assumes two decimal places.
 */

export interface CurrencyInfo {
  code: string;
  symbol: string | null;
  exponent: number;
}

export const DEFAULT_CURRENCY: CurrencyInfo = { code: 'USD', symbol: '$', exponent: 2 };

export function money(minor: number | string | null | undefined, currency: CurrencyInfo): string {
  if (minor === null || minor === undefined) return '—';
  const n = typeof minor === 'string' ? Number(minor) : minor;
  if (!Number.isFinite(n)) return '—';

  const value = n / 10 ** currency.exponent;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: currency.exponent,
      maximumFractionDigits: currency.exponent,
    }).format(value);
  } catch {
    // An unknown ISO code should degrade to a readable number, not blow up
    // the whole screen.
    return `${currency.symbol ?? currency.code} ${value.toFixed(currency.exponent)}`;
  }
}

/** Compact form for tiles, where the exact cent is noise. */
export function moneyShort(minor: number | string | null | undefined, currency: CurrencyInfo): string {
  if (minor === null || minor === undefined) return '—';
  const n = typeof minor === 'string' ? Number(minor) : minor;
  if (!Number.isFinite(n)) return '—';
  const value = n / 10 ** currency.exponent;
  if (Math.abs(value) >= 10000) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.code,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return money(minor, currency);
}

/**
 * Dates arrive as plain 'YYYY-MM-DD' strings and are formatted without ever
 * constructing a Date. Parsing them would make each one midnight UTC, which
 * displays as the previous day for any club west of Greenwich.
 */
export function date(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

/** Timestamps are instants and are shown in the viewer's zone. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

export function percent(fraction: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 })
    .format(fraction);
}

/** Basis points, as tax rates are stored. 775 -> "7.75%". */
export function rate(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(2).replace(/\.00$/, '')}%`;
}

export function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Parse a typed amount into minor units.
 *
 * Deliberately strict: "12.345" in a 2-decimal currency is a typo, not a
 * value to silently round. Staff key these in under time pressure and a
 * quietly-rounded charge is a dispute weeks later.
 */
export function parseMoney(input: string, currency: CurrencyInfo): number | null {
  const cleaned = input.replace(/[^0-9.\-]/g, '').trim();
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const parts = cleaned.split('.');
  if (parts.length > 2) return null;
  const decimals = parts[1]?.length ?? 0;
  if (decimals > currency.exponent) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10 ** currency.exponent);
}
